import camelCase from 'lodash/camelCase'
import snakeCase from 'lodash/snakeCase'
import getObjectTransformer from 'util/getObjectTransformer'
import getDeepestData from 'util/getDeepestData'

import axios from 'axios'
import PendingQueries from 'singletons/PendingQueries'
import User from 'singletons/User'

import Requests, { ApiQueriesSuccessfulResponseTypes, ApiQueriesRequestTypes } from './requests/cooked/queries'

import reportError from 'util/reportError'
import { ValidationError } from 'yup'
import parseBackendErrors from 'util/parseBackendErrors'

export const fromApi = getObjectTransformer(camelCase)

const convertEmptyStringToNull = (v: string) => (v === '' ? null : v)

/*
  _id is for mongo;
*/
export const toApiNormal = getObjectTransformer(
  (k: string) => (k === 'id' ? '_id' : snakeCase(k)),
  convertEmptyStringToNull,
)

const toApiWithoutSnake = getObjectTransformer((k: string) => (k === 'id' ? '_id' : k), convertEmptyStringToNull)

type MakeQueryOptions = {
  tokenOption?: 'mfa' | 'default'
  shouldThrowAllErrors?: boolean
  signal?: AbortSignal
}

async function makeQuery<K extends keyof ApiQueriesRequestTypes & keyof typeof Requests>(
  fetchType: K,
  params: ApiQueriesRequestTypes[K],
  { tokenOption = 'default', shouldThrowAllErrors, signal }: MakeQueryOptions = {},
): Promise<ApiQueriesSuccessfulResponseTypes[K]> {
  const {
    url: getUrl,
    method,
    getBody,
    baseURL,
    Schema,
    doNotConvertKeysToSnakeCase,
    responseType,
  } = Requests[fetchType]

  // Optional flag on some requests; not present on all
  const shouldNotLogoutOn403Error = (Requests as any)[fetchType]?.shouldNotLogoutOn403Error

  /*
    actions should not be called inside component observers.
    makeQuery is often used in pair with onBecomeObserved, which happens inside render.
    to avoid react errors, PendingQueries.add must be performed asynchronously
  */

  let queryId

  try {
    await Schema.validate(params)
    queryId = PendingQueries.add(fetchType, params)
    const toApi = doNotConvertKeysToSnakeCase ? toApiWithoutSnake : toApiNormal
    let token
    if (tokenOption === 'mfa') {
      token = User.mfa.token
    } else {
      token = User.auth.token
    }
    const body = await getBody.call(undefined, params)

    // Create axios cancel token from AbortSignal for compatibility with axios 0.21.1
    let cancelToken
    if (signal) {
      const source = axios.CancelToken.source()
      cancelToken = source.token

      // Connect AbortSignal to axios CancelToken
      signal.addEventListener('abort', () => {
        source.cancel('Request aborted')
      })
    }

    const response = await axios({
      headers: {
        'Content-Type': 'application/json',
        Authorization: token && `Bearer ${token}`,
      },
      method,
      baseURL,
      responseType,
      url: getUrl.call(undefined, params),
      // must return undefined for axios and null for fetch to indicate body is empty
      data: body ? toApi(body) : undefined,
      shouldNotLogoutOn403Error,
      cancelToken,
    })
    return fromApi(getDeepestData(response))
  } catch (e) {
    if (e instanceof ValidationError) {
      console.warn('Query params: ', params, 'Error path: ', e.path)
      console.dir(e)
      reportError('PROMISE', `Error during query validation: ${fetchType}`)
      if (shouldThrowAllErrors) throw e
    } else if (axios.isCancel(e)) {
      // Convert axios cancel error to AbortError for consistency with AbortController pattern
      const abortError = new Error('Request aborted')
      abortError.name = 'AbortError'
      throw abortError
    } else throw parseBackendErrors(e)
  } finally {
    PendingQueries.remove(fetchType, queryId)
  }
}

export const reactQueryRequest = async <K extends keyof ApiQueriesRequestTypes & keyof typeof Requests>(
  fetchType: K,
  params: ApiQueriesRequestTypes[K],
  options: MakeQueryOptions = {},
) => {
  return makeQuery(fetchType, params, { ...options, shouldThrowAllErrors: true })
}

export default makeQuery
